import { Badge } from '@/components/ui/badge';
import {
  WebhookDeliveryStatus,
  type WebhookDeliveryStatus as DeliveryStatus,
} from '@/lib/api/schemas/webhookDeliveryStatus';
import {
  WebhookEventStatus,
  type WebhookEventStatus as EventStatus,
} from '@/lib/api/schemas/webhookEventStatus';
import { cn } from '@/lib/utils';

type Kind = 'event' | 'delivery';

type AnyStatus = EventStatus | DeliveryStatus;

interface Props {
  kind: Kind;
  status: AnyStatus;
  className?: string;
}

/**
 * Single badge that knows how to render either a `WebhookEventStatus` or a
 * `WebhookDeliveryStatus`. The two enums overlap on `Delivered/Failed/Abandoned`
 * but diverge on the in-flight states (event has Received/Dispatching, delivery
 * has Pending/Delivering).
 */
export function WebhookStatusBadge({ kind, status, className }: Props) {
  const variant = (() => {
    if (kind === 'event') {
      switch (status) {
        case WebhookEventStatus.Received:
          return 'secondary';
        case WebhookEventStatus.Dispatching:
          return 'warning';
        case WebhookEventStatus.Delivered:
          return 'success';
        case WebhookEventStatus.Failed:
          return 'warning';
        case WebhookEventStatus.Abandoned:
          return 'destructive';
        default:
          return 'outline';
      }
    }
    switch (status) {
      case WebhookDeliveryStatus.Pending:
        return 'secondary';
      case WebhookDeliveryStatus.Delivering:
        return 'warning';
      case WebhookDeliveryStatus.Delivered:
        return 'success';
      case WebhookDeliveryStatus.Failed:
        return 'warning';
      case WebhookDeliveryStatus.Abandoned:
        return 'destructive';
      default:
        return 'outline';
    }
  })();

  // Pulse the in-flight states (Received / Dispatching / Pending / Delivering)
  // to give a hint that something's happening server-side.
  const pulsing =
    status === WebhookEventStatus.Dispatching ||
    status === WebhookEventStatus.Received ||
    status === WebhookDeliveryStatus.Pending ||
    status === WebhookDeliveryStatus.Delivering;

  return (
    <Badge
      variant={variant as 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'}
      className={cn(pulsing && 'motion-safe:animate-pulse', className)}
    >
      {status}
    </Badge>
  );
}
